import React from 'react';
import { Link } from 'react-router-dom';
import gql from 'graphql-tag';
import { registerClass, currySC, IFNTheme, cns } from '@food/css-manager';
import { oldMediaToGraphqlMedia } from '../../utils/data';
import { DefaultLayout } from '../../utils/layout';
import { IPageData } from '../DefaultPageData';
import { Query } from 'react-apollo';
import dayjs from 'dayjs';
import { EditorialSection, Magazine as MagazineType } from '../../server-types';
import { canView } from '../../utils/canView';
import { isLogged } from '@food/auth/build/dist/logic/auth';
import { NotVisibleAction } from '../NotVisibleAction';
import { Spinner } from '../Spinner';
import { useTranslation } from 'react-i18next';
import { MagazineCollectionHistory } from '../MagazineCollectionHistory';
//import { SubscriptionBannerStatusAccess } from '../SubscriptionBannerStatusAccess';
import { SubscriptionBannerStatusActive } from '../SubscriptionBannerStatusActive';
import { magazineCardFragment } from '../../utils/fragments';
//import { hasSubscription } from '../../utils/hasSubscription';
import { useUserMagazineData } from '../../utils/hooks';
import { config } from '../../utils/config';
import { EditorialSectionOrCollectionHeader } from '../EditorialSectionOrCollectionHeader';

const editorialSectionQuery = gql`
    query EditorialSectionQuery($editorialSectionId: ID!) {
        editorialSection: node(id: $editorialSectionId) {
            ... on EditorialSection {
                id
                name,
				color,
                iconMedia {
                    id,
                    vecThumbUrl,
                    mediumThumbUrl,
					origUrl
                }
            }
        }
    }
`;
const magazineDetailQuery = gql`
	query MagazineDetailQuery($magazineId: ID!) {
		magazine: node(id: $magazineId) {
			... on Magazine {
				id
				name
				title
				category
				issueNumber
				numOfTheYear
				publicationDate
				numPages
				featuredImageSizes
				featuredImageUrl
				customVisibilityType
				customVisibilityArgs
				previewUrl
				prefixPages
				featuredImageMedia {
					id
					smallThumbUrl
					mediumThumbUrl
				}
				Media {
					id
					mediumThumbUrl
				}
				magazineContents {
					id
					name
					pageNumber
				}
				MagazineCollection {
					id
					name
					BrandSf
					startYear
					visibilityType
					visibilityArgs
				}
			}
		}
		magazines {
			edges {
				node {
					...MagazineCardFragment
				}
			}
		}
	}
	${magazineCardFragment}
`;

const styleClass = registerClass(
	(t: IFNTheme) => `

	.${sc('section')} {
		padding: ${t.ratios.xl * t.font.baseSize}px 0 ${t.ratios.l * t.font.baseSize}px;
	}

	.${sc('header')} {
		display: flex;
		align-items: center;
		justify-content: center;
		
		&.${sc('layout', DefaultLayout.TABLET)}, &.${sc('layout', DefaultLayout.MOBILE)} {
			flex-wrap: wrap;
		}
	}

	.${sc('imageContainer')} {
		margin-right: ${t.ratios.xl * t.font.baseSize}px;
		flex-shrink: 0;
		max-width: 100%;
		
		img {
			max-width: ${20 * t.font.baseSize}px;  // AP Numerico, ci vuole una misura massima
			width: 100%;
			box-shadow: ${t.boxShadow(1)};
		}
		
		&.${sc('layout', DefaultLayout.TABLET)}, &.${sc('layout', DefaultLayout.MOBILE)} {
			margin: ${t.ratios.xl * t.font.baseSize}px 0;
		
			&.${sc('layout', DefaultLayout.TABLET)} {
				padding: 0 15%;
			}
		
			&.${sc('layout', DefaultLayout.MOBILE)} {
				padding: 0 10%;
			}
		}
	}
	
	.${sc('infoContainer')} {
		max-width: 600px;

		&.${sc('layout', DefaultLayout.TABLET)}, &.${sc('layout', DefaultLayout.MOBILE)} {
			text-align: center;
		}
	}

	.${sc('title')} {
		font-size: ${t.ratios.xl}em;
		font-weight: ${t.font.weights.bold};
		margin-bottom: ${t.ratios.l * t.font.baseSize}px;
	}
	
	.${sc('infoText')} {
		margin-bottom: ${t.ratios.xs * t.font.baseSize}px;
		font-weight: ${t.font.weights.semibold};
		line-height: ${t.ratios.xl};
	}

	.${sc('notVisibleAction')} {
		margin-top: ${t.ratios.l}em;
	}
	
	.${sc('layoutSwitcher')} {
	
		&.${sc('layout', DefaultLayout.MOBILE)} {
			display: flex;
			justify-content: center;
			white-space: pre;
		}
	}
	
	.${sc('button')} {
		padding: ${(t.ratios.xs / 3) * 2 * t.font.baseSize}px ${t.ratios.xl * t.font.baseSize}px;
		border-radius: ${t.radius * t.font.baseSize}px;
		font-weight: ${t.font.weights.bold};
		color: ${t.colors.white};
		display: inline-flex;
		margin-top: ${t.ratios.xl * t.font.baseSize}px;
	}
	
	.${sc('buttonYellow')} {
		background-color: ${t.colors.alt2.plain};
	}
	
	.${sc('buttonGreen')} {
		background-color: ${t.colors.success.plain};
	}

	.${sc('buttonBlue')} {
		background-color: ${t.colors.alt.dark};
	}
	
	.${sc('mainTitle')} {
		margin-bottom: ${t.ratios.l * t.font.baseSize}px;
	
		> h2 {
			font-size: ${t.ratios.xl}em;
			font-weight: ${t.font.weights.bold};
		}
		
		> .${sc('infolink')} {
			line-height: 1.4;
		}
	}
	
	.${sc('tabContainer')} {

		.${sc('tabSection')} {
			font-size: ${t.ratios.l}em;
			margin-top: ${t.ratios.s * t.font.baseSize}px;
			padding-left: ${t.ratios.s * t.font.baseSize}px;
			
			&:first-child {
				margin-top: 0;
			}

			a, span {
				display: flex;
				
				.${sc('tabTitle')} {
					line-height: 1.2;
				}

				.${sc('tabNumber')} {
					color: ${t.colors.grey.plain};
					display: inline-block;
					min-width: 2.3em;
					flex-grow: 0;
					flex-shrink: 0;
					text-align: right;
					line-height: 1.2;
					padding-right: 0.5em;

					&:after {
						content: ".";
						display: inline;
					}
				}

			}
		}
	}
  `,
);

const sc = currySC(styleClass);

export const Magazine: React.FC<IPageData & { magazineId: string }> = ({
	layout,
	magazineId,
}) => {
	const { t } = useTranslation();
	const { loading: loading1, userMagazineData } = useUserMagazineData();

	return (
		<Query<{ magazine: MagazineType }, { magazineId: string }>
			query={magazineDetailQuery}
			variables={{ magazineId }}
		>
			{({ error, loading, data }) => {
				if (error) {
					console.error(error);
					return <span>error</span>;
				}

				if (loading1 || loading || !data) {
					return <Spinner />;
				}

				const { magazine } = data;
				const {
					featuredImageMedia,
					numPages,
					magazineContents,
					issueNumber,
					name,
					id,
					featuredImageSizes,
					numOfTheYear,
					publicationDate,
					MagazineCollection: collection,
				} = magazine;

				const media =
					featuredImageMedia || oldMediaToGraphqlMedia(featuredImageSizes);
				const date = dayjs(publicationDate);
				const publicationYear =
					collection && collection.startYear
						? date.get('year') - collection.startYear
						: null;
				const issue = (issueNumber ? `N.${issueNumber} ` : '') + name;

				const visibilityArgs = {
					visibility: magazine.customVisibilityType || collection.visibilityType,
					args: magazine.customVisibilityType
						? magazine.customVisibilityArgs
						: collection.visibilityArgs,
					user: userMagazineData,
					isLogged: isLogged(),
				};

				const hasVisibility = canView(visibilityArgs);
				//const isSubscribed = visibilityArgs.user ? hasSubscription(visibilityArgs) : false;

				document.title = issue + " - " + t(collection.BrandSf) + " - " + collection.name + " - Knowledge Center";

				// cerco editorial section da config (by name)
				let editorialSectionId: any = null;

				if (magazine.category && (config.KNOWLEDGECENTER_SECTIONS || config.KNOWLEDGECENTER_MAGAZINECOLLECTIONS || config.KNOWLEDGECENTER_VERTICALS)) {
					const filteredCategoryFromConfig = [...config.KNOWLEDGECENTER_SECTIONS, ...config.KNOWLEDGECENTER_MAGAZINECOLLECTIONS, ...config.KNOWLEDGECENTER_VERTICALS].filter((v) => {
						return (typeof (v) !== 'object') ?
							false :
							(!v.name) ?
								false :
								(magazine.category && v.name.toLowerCase() === magazine.category.toLowerCase()) ?
									true :
									false;
					});
					editorialSectionId = (filteredCategoryFromConfig.length) ? filteredCategoryFromConfig[0].id : null;
				}
				// cerco collection da config (by id)
				let collectionLabel: any = null;
				let collectionFromConfig: any = null;
				if (magazine.MagazineCollection && config.KNOWLEDGECENTER_MAGAZINECOLLECTIONS) {
					const filteredCollectionFromConfig = [...config.KNOWLEDGECENTER_MAGAZINECOLLECTIONS].filter((v) => {
						return (typeof (v) !== 'object') ?
							false :
							(!v.name) ?
								false :
								(magazine.MagazineCollection.id && v.id === magazine.MagazineCollection.id) ?
									true :
									false;
					});
					collectionFromConfig = (filteredCollectionFromConfig.length) ? filteredCollectionFromConfig[0] : null;
					collectionLabel = (collectionFromConfig && collectionFromConfig.label) ? collectionFromConfig.label : magazine.MagazineCollection.name;
				}

				return (
					<div className={styleClass}>
						{/*hasVisibility && !isSubscribed && (
							<SubscriptionBannerStatusAccess visibilityArgs={visibilityArgs} />
						)*/}
						<SubscriptionBannerStatusActive user={visibilityArgs.user} />

						{editorialSectionId ? <Query<{ editorialSection: EditorialSection }, { editorialSectionId: string }>
							query={editorialSectionQuery}
							variables={{ editorialSectionId }}
						>
							{({ error, loading, data }) => {
								if (error) {
									console.error(error);
									return <span>error</span>;
								}

								if (loading || !data) {
									return <Spinner />;
								}

								const { editorialSection } = data;

								// recupero le informazioni dal config
								const configEditorialSection = config.KNOWLEDGECENTER_VERTICALS.concat(config.KNOWLEDGECENTER_SECTIONS).filter((v: any) => typeof (v) === 'object' ? (v.id === editorialSection.id) : v === editorialSection.id)[0];

								let aLinks = [] as { url: string; name: string, iframe?: boolean }[];
								if (configEditorialSection.news_link) {
									aLinks.push({
										url: '' + configEditorialSection.news_link,
										name: 'Guarda tutte le news'
									});
								}
								if (configEditorialSection.newsletter_link) {
									aLinks.push({
										url: '' + configEditorialSection.newsletter_link,
										name: 'Iscriviti alla newsletter',
										iframe: true
									});
								}

								return (
									<EditorialSectionOrCollectionHeader
										layout={layout ? layout : DefaultLayout.DESKTOP}
										media={editorialSection.iconMedia && (editorialSection.iconMedia.vecThumbUrl || editorialSection.iconMedia.origUrl)}
										color={editorialSection.color ? editorialSection.color : null}
										title={editorialSection.name}
										links={aLinks}
										logoLink={'/category/' + editorialSection.id}
									/>
								);
							}}
						</Query> :
							(collectionLabel && <EditorialSectionOrCollectionHeader
								layout={layout ? layout : DefaultLayout.DESKTOP}
								title={collectionLabel}
								logoLink={(collectionFromConfig) ? '/collection/' + collectionFromConfig.id : ''}
							/>)}

						<header
							className={cns(sc('header'), sc('layout', layout), sc('section'))}
						>
							<div className={cns(sc('imageContainer'), sc('layout', layout))}>
								{media && (
									<img
										src={media.mediumThumbUrl}
										alt={'magazine cover thumbnail'}
									/>
								)}
							</div>

							<div className={cns(sc('infoContainer'), sc('layout', layout))}>
								<div className={sc('title')}>{issue}</div>
								<div className={sc('infoText')}>
									{t(collection.BrandSf)} - {collection.name}
								</div>
								{collection.startYear && numOfTheYear && (
									<div className={sc('infoText')}>
										{t`Year`} {publicationYear} - {t`number_short_notation`}{' '}
										{numOfTheYear}
									</div>
								)}
								<div className={sc('infoText')}>
									{date.format('MM/YYYY')} | {numPages}{' '}
									{t`page_short_notation`}
								</div>
								{hasVisibility && (
									<Link
										className={cns(sc('button'), sc('buttonBlue'))}
										to={`/magazine/${id}/read`}
									>
										{t`read_magazine`}
									</Link>
								)}
								{!hasVisibility && (
									<NotVisibleAction
										visibilityArgs={visibilityArgs}
										magazine={magazine}
										layout={layout}
									/>
								)}
							</div>
						</header>

						<main className={sc('mainIndex')}>
							{magazineContents && magazineContents.length > 0 && (
								<>
									<section className={sc('section')}>
										<div className={sc('mainTitle')}>
											<h2>{t`index`}</h2>
										</div>

										<div className={sc('tabContainer')}>
											{magazineContents.map((content: any) => (
												<div
													key={id}
													className={cns(
														sc('tabSection'),
														sc('text'),
														sc('layout', layout),
													)}
												>
													{hasVisibility && (
														<Link
															key={content.id}
															to={{
																pathname: `/magazine/${id}/read`,
																search: `page=${content.pageNumber +
																	(magazine.prefixPages || 0)}`,
															}}
														>
															<span className={sc('tabNumber')}>
																{content.pageNumber}
															</span>
															<span className={sc('tabTitle')}>
																{content.name}
															</span>
														</Link>
													)}
													{!hasVisibility && (
														<span>
															<span className={sc('tabNumber')}>
																{content.pageNumber}
															</span>
															<span className={sc('tabTitle')}>
																{content.name}
															</span>
														</span>
													)}
												</div>
											))}
										</div>
									</section>
								</>
							)}

							<MagazineCollectionHistory
								className={sc('section')}
								collectionId={collection.id}
								layout={layout}
								category={magazine.category ? magazine.category : undefined}
								yearSelection={collection.id != '4aSIksnqH08~f0d' ? true : false}
							/>
						</main>
					</div>
				);
			}}
		</Query >
	);
};
